import { useI18n } from '@/lib/i18n';
import { OrderPackage, OrderPlatform } from '@/types';
import { CheckIcon } from '@heroicons/react/24/outline';
import Instagram from '@images/Instagram.svg';
import TikTok from '@images/TikTok.svg';
import { Link } from '@inertiajs/react';

export default function AvailablePackagesSection({ packages }: { packages: OrderPackage[] }) {
  const { t, formatNumber } = useI18n();

  return (
    <section className="bg-secondary/10 relative">
      <div className="mx-auto max-w-screen-lg p-6 md:py-12 lg:py-20">
        <h2 className="text-center text-4xl font-semibold tracking-tight text-black sm:text-6xl">
          {t('components.available_packages.Available Packages')}
        </h2>

        <p className="mx-auto mt-8 max-w-prose text-center text-lg font-medium text-pretty sm:text-xl">{t('components.available_packages.intro')}</p>

        <div className="mt-8 grid grid-cols-1 items-start gap-4 md:gap-8 lg:mt-16 lg:grid-cols-3">
          {packages.map((p) => (
            <div key={`${p.platform}-${p.interaction}-${p.size}`} className={'rounded-xl bg-white p-4'}>
              {p.platform === OrderPlatform.Instagram ? (
                <img src={Instagram} className={'mx-auto size-8 rounded-xl bg-orange-400/30 p-1.5'} alt={'Instagram Logo'} loading={'lazy'} />
              ) : (
                <img src={TikTok} className={'mx-auto size-8 rounded-xl bg-pink-400/30 p-1.5'} alt={'TikTok Logo'} loading={'lazy'} />
              )}

              <h3 className={'mt-2 text-center text-4xl font-bold'}>{formatNumber(p.size)}</h3>
              <h4 className={'text-center text-sm font-semibold text-gray-500 uppercase'}>
                {t(`packages.${p.platform}.title`)} {t(`packages.${p.interaction}`)}
              </h4>

              <div className="mt-4 border-t border-gray-100 p-4">
                <div className="flex items-center">
                  <CheckIcon className={'mr-2 size-5'} />
                  <div className={''}>{t('components.available_packages.High-Quality :interaction', { interaction: p.interaction })}</div>
                </div>

                <div className="flex items-center">
                  <CheckIcon className={'mr-2 size-5'} />
                  <div className={''}>{t('components.available_packages.Instant start')}</div>
                </div>

                <div className="flex items-center">
                  <CheckIcon className={'mr-2 size-5'} />
                  <div className={''}>{t('components.available_packages.No password required')}</div>
                </div>

                <div className="mb-4 flex items-center">
                  <CheckIcon className={'mr-2 size-5'} />
                  <div className={''}>{t('components.available_packages.Support 247')}</div>
                </div>

                {p.percentOff > 0 ? (
                  <div className="text-center text-gray-500 line-through">{formatNumber(p.price / (p.percentOff / 100), true)}</div>
                ) : (
                  <div>&nbsp;</div>
                )}
                <div className="text-center text-2xl font-bold text-red-600">{formatNumber(p.price, true)}</div>
              </div>

              <Link
                href={route(`${p.platform}.${p.interaction}`, { size: p.size })}
                className={'flex items-center rounded-xl bg-black px-3.5 py-3 text-center font-bold text-white hover:opacity-75 md:text-base'}
              >
                {p.percentOff > 0 && (
                  <div className={'mr-2 flex items-center justify-center rounded-xl bg-green-400 px-2 py-0.5 text-green-950'}>
                    <div className={'text-xs font-semibold'}>{t('components.available_packages.:amount % off', { amount: p.percentOff })}</div>
                  </div>
                )}
                {t('components.available_packages.Buy Package')} &rarr;
              </Link>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
